type Env = 'development' | 'production';

const rootPaths: Record<Env, string> = {
  development: 'https://webfonts.development.dev.woltapi.com',
  production: 'https://webfonts.wolt.com',
};

type FontFace =
  | 'm-plus-rounded-1c'
  | 'OmnesGreek'
  | 'OmnesArabic'
  | 'OmnesCyrillic'
  | 'OmnesGeorgian'
  | 'OmnesHebrew'
  | 'OmnesLatin';

const fontFacePaths: Record<FontFace, string[]> = {
  'm-plus-rounded-1c': [
    'M-PLUS-Rounded-1c/m-plus-rounded-1c-v10-latin_japanese-700',
    'M-PLUS-Rounded-1c/m-plus-rounded-1c-v10-latin_japanese-900',
  ],
  OmnesGreek: ['Omnes/OmnesGreek-SemiBold', 'Omnes/OmnesGreek-Bold'],
  OmnesArabic: ['Omnes/OmnesArabic-SemiBold', 'Omnes/OmnesArabic-Bold'],
  OmnesCyrillic: ['Omnes/OmnesCyrillic-SemiBold', 'Omnes/OmnesCyrillic-Bold'],
  OmnesGeorgian: ['Omnes/OmnesGeorgian-SemiBold', 'Omnes/OmnesGeorgian-Bold'],
  OmnesHebrew: ['Omnes/OmnesHebrew-Bold', 'Omnes/OmnesHebrew-Black'],
  OmnesLatin: ['Omnes/OmnesLatin-SemiBold', 'Omnes/OmnesLatin-Bold'],
};

const fontFacesByLanguage: Record<string, FontFace[]> = {
  ar: ['OmnesArabic', 'OmnesLatin'],
  bg: ['OmnesCyrillic', 'OmnesLatin'],
  el: ['OmnesGreek', 'OmnesLatin'],
  he: ['OmnesHebrew', 'OmnesLatin'],
  ja: ['m-plus-rounded-1c'],
  ka: ['OmnesGeorgian', 'OmnesLatin'],
  kk: ['OmnesCyrillic', 'OmnesLatin'],
  mk: ['OmnesCyrillic', 'OmnesLatin'],
  ru: ['OmnesCyrillic', 'OmnesLatin'],
  sr: ['OmnesCyrillic', 'OmnesLatin'],
  uk: ['OmnesCyrillic', 'OmnesLatin'],
};

const DEFAULT_FONT_FACES: FontFace[] = ['OmnesLatin'];

const getFontFacePath = (env: Env, fontFilePath: string) =>
  `${rootPaths[env]}/fonts/${fontFilePath}.woff2?v=1`;

function parseLanguageCode(language: string): string {
  const regex = /^[a-z]{2}/i;
  const match = language.match(regex);

  if (!match) {
    throw new Error(`Unable to parse language: '${language}'.`);
  }

  return match[0].toLowerCase();
}

/**
 * @param language Language, e.g. "en" or "en-UK".
 * @param env The intended environment. Controls the used S3 bucket. CORS configuration is more
 *   relaxed on the `development` bucket.
 * @returns Paths for font files that should be preloaded for the given language. Example:
 *
 *   ```js
 *   [
 *     'https://webfonts.wolt.com/fonts/Omnes/OmnesLatin-SemiBold.woff2?v=1',
 *     'https://webfonts.wolt.com/fonts/Omnes/OmnesLatin-Bold.woff2?v=1',
 *   ];
 *   ```
 *
 *   Place the paths in `link` tags in the `head` element. Example:
 *
 *   ```html
 *   <link rel="preload" href="path_to_font_file_1" as="font" crossorigin="anonymous" />,
 *   <link rel="preload" href="path_to_font_file_2" as="font" crossorigin="anonymous" />,
 *   ```
 */
export function getPreloadFontFileUrlsByLanguage(language: string, env: Env): string[] {
  const lang = parseLanguageCode(language);

  return (fontFacesByLanguage[lang] ?? DEFAULT_FONT_FACES)
    .flatMap((fontFace) => fontFacePaths[fontFace])
    .map((path) => getFontFacePath(env, path));
}
